import { useCallback } from 'react';
import { closeMenu } from '@client/hooks/use-menu.hooks';
import { useProjectId } from '@client/store/project/project.hook';
import { getItemById } from '@shared/helpers/array.helpers';
import {
  formatBlueprintStoreyLevelLabel,
  getBlueprintStoreyAssignmentStartLevel,
  getBlueprintStoreyStackSize,
  getBlueprintUnderlaysInView,
  getNormalizedBlueprintUnderlays,
  removeBlueprintUnderlayById,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { reportDesignPersistError } from '../helpers/design-snapshot-persist.helpers';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  getBuildingDesign,
  persistBlueprintUnderlayById,
  persistBlueprintUnderlays,
  useActiveBlueprintUnderlay,
  useBlueprintUnderlayBuildingId,
  useBlueprintUnderlays,
  useUpdateBuildingDesign,
} from './design.hooks';
import { useStoreys } from './storeys.hooks';
import {
  useIsBuildingSelected,
  useSetBlueprintInteractionMode,
  useSetCalibratePoints,
  useSetManualBlueprintUnderlayId,
  useSetShowBlueprintUnderlay,
  useShowBlueprintUnderlay,
} from '../store/design-store.hooks';
import { getBlueprintCopyTargetLevels } from '../helpers/blueprint-underlay-page-menu.helpers';
import {
  appendBlueprintUnderlayCopy,
  blueprintUnderlayCopyNeedsRasterFetch,
} from '../helpers/blueprint-underlay-raster-hydration.helpers';
import { hydrateBlueprintRaster } from '../helpers/blueprint-underlay-persist.helpers';

/** Shown when copying a page cannot recover its drawing. */
const BLUEPRINT_UNDERLAY_COPY_FAILED =
  'Could not copy the underlay. Please try again.';

/**
 * Shared page actions for the underlay kebab / right-click menu and Backspace.
 */
export const useBlueprintUnderlayPageActions = () => {
  const projectId = useProjectId();
  const underlay = useActiveBlueprintUnderlay();
  const underlays = useBlueprintUnderlays();
  const buildingId = useBlueprintUnderlayBuildingId();
  const buildingSelected = useIsBuildingSelected();
  const storeys = useStoreys();
  const storeyLevelCount = getBlueprintStoreyStackSize(storeys);
  const updateBuildingDesign = useUpdateBuildingDesign();
  const setManualUnderlayId = useSetManualBlueprintUnderlayId();
  const setMode = useSetBlueprintInteractionMode();
  const setCalibratePoints = useSetCalibratePoints();
  const showBlueprintUnderlay = useShowBlueprintUnderlay();
  const setShowBlueprintUnderlay = useSetShowBlueprintUnderlay();
  const { enqueueSnackbar } = useSnackbar();
  const errorHandler = useErrorHandler();

  /**
   * Reset PDF selection chrome after the underlay list becomes empty.
   */
  const clearPdfSelectionChrome = useCallback(() => {
    setMode('locked');
    setCalibratePoints([]);
    setManualUnderlayId(undefined);
  }, [setCalibratePoints, setManualUnderlayId, setMode]);

  /**
   * Whether the map actually draws this page.
   */
  const isShownOnMap = useCallback(
    (pageId: string) => {
      const page = getItemById(underlays, pageId, false);
      if (!page) return false;
      return page.visible && showBlueprintUnderlay && page.id === underlay?.id;
    },
    [showBlueprintUnderlay, underlay?.id, underlays],
  );

  /**
   * Storey levels this page can be copied onto.
   */
  const copyTargetLevelsFor = useCallback(
    (pageId: string) => {
      const page = getItemById(underlays, pageId, false);
      const startLevel = page
        ? getBlueprintStoreyAssignmentStartLevel(page.storeyAssignment, storeys)
        : undefined;
      return getBlueprintCopyTargetLevels(storeyLevelCount, startLevel);
    },
    [storeyLevelCount, storeys, underlays],
  );

  /**
   * Delete one page. Last page also clears map selection chrome.
   */
  const removePage = useCallback(
    async (pageId: string) => {
      closeMenu();
      // Patch the live list after the persist lock so pages placed while
      // waiting are not dropped.
      try {
        await persistBlueprintUnderlays(
          (live) => removeBlueprintUnderlayById(live, pageId),
          updateBuildingDesign,
        );
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
        return;
      }
      const remaining = getBlueprintUnderlaysInView(
        getNormalizedBlueprintUnderlays(getBuildingDesign()),
        { buildingId, buildingSelected },
      );
      if (underlay?.id === pageId) {
        setManualUnderlayId(remaining[0]?.id);
      }
      if (!remaining.length) clearPdfSelectionChrome();
    },
    [
      buildingId,
      buildingSelected,
      clearPdfSelectionChrome,
      errorHandler,
      setManualUnderlayId,
      underlay?.id,
      updateBuildingDesign,
    ],
  );

  /**
   * Show or hide a page on the map.
   */
  const toggleShownOnMap = useCallback(
    async (pageId: string) => {
      closeMenu();
      const page = getItemById(underlays, pageId, false);
      if (!page) return;
      if (isShownOnMap(pageId)) {
        try {
          await persistBlueprintUnderlayById(
            { ...page, visible: false },
            updateBuildingDesign,
          );
        } catch (error: unknown) {
          reportDesignPersistError(error, errorHandler);
        }
        return;
      }
      if (!showBlueprintUnderlay) setShowBlueprintUnderlay(true);
      setManualUnderlayId(pageId);
      if (page.visible) return;
      try {
        await persistBlueprintUnderlayById(
          { ...page, visible: true },
          updateBuildingDesign,
        );
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
      }
    },
    [
      errorHandler,
      isShownOnMap,
      setManualUnderlayId,
      setShowBlueprintUnderlay,
      showBlueprintUnderlay,
      underlays,
      updateBuildingDesign,
    ],
  );

  /**
   * Place a second copy of this page on another storey.
   * Fetches that page's drawing first when it is not already stored or cached.
   * A failed fetch shows an error and does not save the copy.
   */
  const copyToStorey = useCallback(
    async (pageId: string, startLevel: number) => {
      closeMenu();
      const page = getItemById(underlays, pageId, false);
      if (!page) return;
      // One GET only when the row has no imageRef and the raster is not cached.
      if (blueprintUnderlayCopyNeedsRasterFetch(page)) {
        const raster = await hydrateBlueprintRaster(projectId, page);
        // undefined means the GET failed; saving would store a page with no drawing.
        if (!raster) {
          enqueueSnackbar(BLUEPRINT_UNDERLAY_COPY_FAILED, { variant: 'error' });
          return;
        }
      }
      let copied = false;
      try {
        await persistBlueprintUnderlays((live) => {
          const next = appendBlueprintUnderlayCopy(live, pageId, startLevel);
          // Same reference means the source page was removed while this waited.
          copied = next !== live;
          return next;
        }, updateBuildingDesign);
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
        return;
      }
      if (!copied) return;
      enqueueSnackbar(
        `Underlay copied to ${formatBlueprintStoreyLevelLabel(startLevel)}`,
        { variant: 'success' },
      );
    },
    [enqueueSnackbar, errorHandler, projectId, underlays, updateBuildingDesign],
  );

  /**
   * Measure this page's scale on the map.
   */
  const setScale = useCallback(
    (pageId: string) => {
      closeMenu();
      setManualUnderlayId(pageId);
      setCalibratePoints([]);
      setMode('calibrate');
    },
    [setCalibratePoints, setManualUnderlayId, setMode],
  );

  return {
    copyTargetLevelsFor,
    copyToStorey,
    isShownOnMap,
    removePage,
    setScale,
    toggleShownOnMap,
  };
};
